import { useCallback, useSyncExternalStore } from 'react'
import { en, type MessageKey } from './en'
import { ar } from './ar'

/*
 * Minimal per-locale resource loader for the V1.9.1 shell (Phase 0).
 * The stored value is the ACCOUNT's language preference — one field serves
 * interface + comms for V1 (PRODUCT-RULES.md §Localisation). Launch pair is
 * English + Arabic; Arabic runs the whole document RTL.
 *
 * Module-level store (not a provider) so any component can call useLocale()
 * and every subscriber re-renders on change.
 */

export type Lang = 'en' | 'ar'
export type Dir = 'ltr' | 'rtl'
export type { MessageKey }

/** localStorage key for the persisted language preference (handoff §State: `uiLang`). */
export const UI_LANG_KEY = 'uiLang'

const MESSAGES: Record<Lang, Record<MessageKey, string>> = { en, ar }

export const dirFor = (lang: Lang): Dir => (lang === 'ar' ? 'rtl' : 'ltr')

function readStored(): Lang {
  try {
    return localStorage.getItem(UI_LANG_KEY) === 'ar' ? 'ar' : 'en'
  } catch {
    return 'en'
  }
}

let current: Lang = readStored()
const listeners = new Set<() => void>()

/** Stamp <html lang dir> so RTL flows from the document root — grid columns,
 *  logical properties and the [dir='rtl'] font swap all follow it. */
function applyToDocument(lang: Lang) {
  document.documentElement.lang = lang
  document.documentElement.dir = dirFor(lang)
}

// Apply the persisted preference at module load so the first paint is already
// in the right direction/language.
applyToDocument(current)

export function setLang(lang: Lang) {
  if (lang === current) return
  current = lang
  try {
    localStorage.setItem(UI_LANG_KEY, lang)
  } catch {
    // Persistence unavailable (private mode etc.) — the in-session choice still applies.
  }
  applyToDocument(lang)
  listeners.forEach(l => l())
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

const getSnapshot = () => current

/** Interpolation params for `{name}`-style holes in a resource string. */
export type MessageParams = Record<string, string | number>

/** Translate function shape shared by components and the pure view-model
 *  helpers (Phase 2): resolve a key in the active locale and fill `{holes}`. */
export type TranslateFn = (key: MessageKey, params?: MessageParams) => string

/** Fill `{name}` holes. A hole with no supplied param is left verbatim so a
 *  missing param is VISIBLE in review, never silently blanked (QA §12.2). */
export function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (hole, name: string) =>
    name in params ? String(params[name]) : hole,
  )
}

/** Locale-independent resolver — lets pure model unit tests translate against
 *  a fixed locale without the React hook. */
export function translate(lang: Lang, key: MessageKey, params?: MessageParams): string {
  return interpolate(MESSAGES[lang][key] ?? en[key], params)
}

export function useLocale(): {
  t: TranslateFn
  lang: Lang
  setLang: (lang: Lang) => void
  dir: Dir
} {
  const lang = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const t = useCallback<TranslateFn>((key, params) => translate(lang, key, params), [lang])
  return { t, lang, setLang, dir: dirFor(lang) }
}
